/**
 * Breadcrumbs — single-line Jira-style trail rendered above a page's h1.
 * The last item is always the current page (plain text, no link). Earlier
 * items link via react-router when `to` is provided; when it is omitted
 * (e.g. a section with no landing page of its own, like "Projects") they
 * render unlinked in the same base color.
 */
import type { CSSProperties } from 'react';
import { Link } from 'react-router';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

const wrapperStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  marginBottom: 4,
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-xs)',
  color: 'var(--text-subtle)',
};

const linkStyle: CSSProperties = {
  color: 'var(--text-subtle)',
  textDecoration: 'none',
};

const separatorStyle: CSSProperties = {
  color: 'var(--text-subtle)',
};

export function Breadcrumbs({ items }: BreadcrumbsProps) {
  if (items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" style={wrapperStyle}>
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {isLast ? (
              <span style={{ color: 'var(--text-muted)' }}>{item.label}</span>
            ) : item.to ? (
              <Link
                to={item.to}
                style={linkStyle}
                onMouseEnter={(e) => {
                  const el = e.currentTarget;
                  el.style.textDecoration = 'underline';
                  el.style.color = 'var(--brand)';
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget;
                  el.style.textDecoration = 'none';
                  el.style.color = 'var(--text-subtle)';
                }}
              >
                {item.label}
              </Link>
            ) : (
              <span>{item.label}</span>
            )}
            {!isLast && <span aria-hidden="true" style={separatorStyle}>&nbsp;/&nbsp;</span>}
          </span>
        );
      })}
    </nav>
  );
}
